import { useCallback } from 'react';
import {
  useReactFlow,
  FitViewOptions,
  Node as ReactFlowNode,
  SetCenterOptions,
} from '@xyflow/react';
import { CanvasElementType } from './canvas-types';
import { useTimeout } from '../../hooks/useTimeout';

const DEFAULT_DURATION = 800;

const isAnimationDisabled = () => {
  const disabled =
    typeof window !== 'undefined' && !!(window as any).__SWEEP_DISABLE_CANVAS_ANIMATION__;

  return disabled;
};

export const useSweepCanvasFitView = () => {
  const { setSweepTimeout } = useTimeout();
  const reactFlowInstance = useReactFlow();

  const setCenter = useCallback(
    (x: number, y: number, options?: SetCenterOptions) => {
      reactFlowInstance.setCenter(x, y, {
        ...options,
        ...(isAnimationDisabled() && { duration: 0 }),
      });
    },
    [reactFlowInstance],
  );

  const fitView = useCallback(
    (options?: FitViewOptions) => {
      const nodes = reactFlowInstance
        .getNodes()
        .filter((node) => node.type !== CanvasElementType.GROUP);

      reactFlowInstance.fitView({
        nodes,
        padding: 0.05,
        ...options,
        ...(isAnimationDisabled() && { duration: 0 }),
      });
    },
    [reactFlowInstance],
  );

  const fitAroundNodes = useCallback(
    ({
      nodeIds,
      options,
      firstChildNodeOnly,
      onAnimationEnd,
    }: {
      nodeIds: string[];
      options?: FitViewOptions;
      firstChildNodeOnly?: boolean;
      onAnimationEnd?: () => void;
    }) => {
      const nodes = reactFlowInstance.getNodes();
      const filteredNodes: ReactFlowNode[] = nodes.filter((node) => nodeIds.includes(node.id));
      let filteredChildNodes: ReactFlowNode[] = [];
      if (firstChildNodeOnly) {
        const node = nodes.find((node) => nodeIds.includes(node.parentId || ''));
        if (node) {
          filteredChildNodes = [node];
        }
      } else {
        filteredChildNodes = nodes.filter((node) => nodeIds.includes(node.parentId || ''));
      }
      filteredNodes.push(...filteredChildNodes);

      let padding = 0.3;
      if (filteredNodes.find((node) => node.type === CanvasElementType.GHOST_NODE)) {
        padding = 1;
      }
      const duration = isAnimationDisabled() ? 0 : (options?.duration || DEFAULT_DURATION);

      reactFlowInstance.fitView({
        nodes: filteredNodes,
        padding,
        ...options,
        duration,
      });
      if (onAnimationEnd) {
        setSweepTimeout(() => {
          onAnimationEnd();
        }, duration + 50);
      }
    },
    [reactFlowInstance, setSweepTimeout],
  );
  return { fitView, fitAroundNodes, setCenter };
};
